import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `${Math.round(Number(n) || 0).toLocaleString('fr-FR')} €`;

export default function MaitriseOeuvreCard({ projectId, onSaved }) {
  const [mo, setMo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [routing, setRouting] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/works-budget`);
      setMo(r.data.works_budget?.maitrise_oeuvre || { enabled: false });
    } catch { setMo({ enabled: false }); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const save = async (next) => {
    setSaving(true);
    setErr('');
    try {
      const r = await axios.put(`${API}/projects/${projectId}/works-budget`, { maitrise_oeuvre: next });
      setMo(r.data.works_budget?.maitrise_oeuvre || next);
      onSaved && onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Erreur de sauvegarde');
    } finally { setSaving(false); }
  };

  const fetchRoute = async () => {
    setRouting(true);
    setErr('');
    try {
      const r = await axios.post(`${API}/projects/${projectId}/mo-route`);
      await save({ ...mo, enabled: true, distance_km: r.data.distance_km, drive_minutes: r.data.drive_minutes });
    } catch (e) {
      setErr(e.response?.data?.detail || "Itinéraire indisponible : saisissez la distance manuellement");
    } finally { setRouting(false); }
  };

  if (!mo) return null;
  const enabled = !!mo.enabled;

  return (
    <div className="realease-card p-5 mt-4" data-testid="mo-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={enabled} data-testid="mo-enabled-checkbox"
                 onChange={(e) => save({ ...mo, enabled: e.target.checked })} />
          <span className="font-semibold text-gray-900">Maîtrise d'œuvre (suivi de chantier)</span>
        </label>
        {enabled && mo.total_ttc != null && (
          <span className="px-3 py-1 rounded-full bg-[#13736D] text-white font-bold text-sm" data-testid="mo-total-ttc">
            {fmt(mo.total_ttc)} TTC
          </span>
        )}
      </div>
      {enabled && (
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Durée travaux (mois)</label>
              <input type="number" min="0" step="0.5" defaultValue={mo.duration_months || ''}
                     key={`d-${mo.duration_months}`}
                     onBlur={(e) => { const v = parseFloat(e.target.value) || 0; if (v !== mo.duration_months) save({ ...mo, duration_months: v }); }}
                     className="realease-input text-sm w-full" data-testid="mo-duration-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Distance Quimperlé (km)</label>
              <input type="number" min="0" step="1" defaultValue={mo.distance_km || ''}
                     key={`k-${mo.distance_km}`}
                     onBlur={(e) => { const v = parseFloat(e.target.value) || 0; if (v !== mo.distance_km) save({ ...mo, distance_km: v, drive_minutes: null }); }}
                     className="realease-input text-sm w-full" data-testid="mo-distance-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Trajet aller (min)</label>
              <input type="number" min="0" step="1" defaultValue={mo.drive_minutes || ''}
                     key={`m-${mo.drive_minutes}`}
                     onBlur={(e) => { const v = parseFloat(e.target.value) || 0; if (v !== mo.drive_minutes) save({ ...mo, drive_minutes: v }); }}
                     className="realease-input text-sm w-full" data-testid="mo-minutes-input" />
            </div>
            <div className="flex items-end">
              <button onClick={fetchRoute} disabled={routing || saving}
                      className="text-xs px-3 py-2 rounded-lg bg-[#008A8D] hover:bg-[#006B6D] text-white font-semibold disabled:opacity-50 w-full"
                      data-testid="mo-route-btn">
                {routing ? 'Calcul...' : 'Itinéraire auto (Quimperlé → projet)'}
              </button>
            </div>
          </div>
          {err && <p className="text-xs text-red-600">{err}</p>}
          {mo.total_ttc != null && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center bg-gray-50 rounded-lg p-3" data-testid="mo-breakdown">
              <div><p className="text-sm font-bold text-gray-800">{mo.visits}</p><p className="text-[10px] text-gray-500">visites (~4,5/mois)</p></div>
              <div><p className="text-sm font-bold text-gray-800">{mo.total_hours} h</p><p className="text-[10px] text-gray-500">mission (61,50 €/h HT)</p></div>
              <div><p className="text-sm font-bold text-gray-800">{fmt(mo.fee_ht)}</p><p className="text-[10px] text-gray-500">honoraires HT</p></div>
              <div><p className="text-sm font-bold text-gray-800">{fmt(mo.km_fees_ht)}</p><p className="text-[10px] text-gray-500">frais km ({mo.km_total} km × 0,60 €)</p></div>
              <div><p className="text-sm font-bold text-[#13736D]">{fmt(mo.total_ht)} HT / {fmt(mo.total_ttc)} TTC</p><p className="text-[10px] text-gray-500">total mission (TVA 20 %)</p></div>
            </div>
          )}
          <p className="text-[10px] text-gray-400">Modèle EDC Agencement : 2 h chantier + 2 h planning/CR par visite + trajet A/R, 16 h réceptions &amp; réserves, 0,60 €/km. Inclus dans le total projet et le camembert.</p>
        </div>
      )}
    </div>
  );
}
